import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { User } from '../../../core/models/user.model';
import { UserStatus } from '../../../core/enums/user-status.enum';
import { UsersService } from '../../../core/services/users.service';
import { AppToastService } from 'app/core/services/app-toast.service';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { UserRoleName } from 'app/core/enums/user-role-name.enum';
import { UserType } from 'app/core/enums/user-type.enum';

import { NgbDropdown, NgbDropdownToggle, NgbDropdownMenu } from '@ng-bootstrap/ng-bootstrap';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'app-user-actions',
  templateUrl: './user-actions.component.html',
  styleUrls: ['./user-actions.component.scss'],
  imports: [NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, RouterLink, FaIconComponent]
})
export class UserActionsComponent implements OnInit {
  private authService = inject(AuthenticationService);
  private userService = inject(UsersService);
  private toastService = inject(AppToastService);

  currentUserId = null;
  userStatuses = UserStatus;
  processing = false;

  @Input() user: User;
  @Output() public userChange = new EventEmitter<User>();

  ngOnInit() {
    this.currentUserId = this.authService.currentUser.id;
  }

  onDeactivateClick($event) {
    $event.preventDefault();

    if (confirm(`Are you sure you want to deactivate ${this.user.fullName}?`)) {
      this.processing = true;

      this.userService.deactivate(this.user).subscribe((user: User) => {
        this.userChange.emit(user);
        this.processing = false;
        this.toastService.show(`"${user.fullName}" has been deactivated.`, {
          cssClass: 'bg-success-subtle',
        });
      });
    }
  }

  onReactivateClick($event) {
    $event.preventDefault();

    if (confirm(`Are you sure you want to reactivate ${this.user.fullName}?`)) {
      this.processing = true;

      this.userService.reactivate(this.user).subscribe((user: User) => {
        this.userChange.emit(user);
        this.processing = false;
        this.toastService.show(`"${user.fullName}" has been reactivated.`, {
          cssClass: 'bg-success-subtle',
        });
      });
    }
  }

  onResetPasswordClick($event) {
    $event.preventDefault();
    this.processing = true;

    this.userService.resetPassword(this.user).subscribe((user: User) => {
      this.userChange.emit(user);
      this.processing = false;
      this.toastService.show(`A reset password email has been sent to "${user.fullName}".`, {
        cssClass: 'bg-success-subtle',
      });
    });
  }

  onUnlockClick($event) {
    $event.preventDefault();
    this.processing = true;

    this.userService.unlock(this.user).subscribe((user: User) => {
      this.userChange.emit(user);
      this.processing = false;
      this.toastService.show(`User ${user.fullName} is now active.`, {
        cssClass: 'bg-success-subtle',
      });
    });
  }

  onResendInvitationClick($event) {
    $event.preventDefault();
    this.processing = true;

    this.userService.resendInvite(this.user).subscribe((user: User) => {
      this.userChange.emit(user);
      this.processing = false;
      this.toastService.show(`Invitation has been resent to "${user.fullName}".`, {
        cssClass: 'bg-success-subtle',
      });
    });
  }

  private get isAdminUser() {
    return this.user.userType === UserType.captureAdmin || this.user.userType === UserType.llmPlaygroundUser;
  }

  private get canManage() {
    return (this.isAdminUser && this.authService.hasPermissionTo(UserRoleName.manageCaptureAdmin)) ||
      !this.isAdminUser && this.authService.hasPermissionTo(UserRoleName.manageUsers);
  }

  get canUnlock() {
    return this.canManage && this.user.status === this.userStatuses.lockedOut;
  }

  get canDeactivate() {
    return this.canManage &&
      this.user.status !== this.userStatuses.disabled &&
      !this.user.cognitoDisabled &&
      this.user.status !== this.userStatuses.missingFromCognito &&
      this.user.id !== this.currentUserId;
  }

  get canReactivate() {
    return this.canManage &&
      (this.user.status === this.userStatuses.disabled || this.user.cognitoDisabled) &&
      this.user.status !== this.userStatuses.invitationSent &&
      this.user.id !== this.currentUserId;
  }

  get canResetPassword() {
    return this.canManage &&
      (this.user.status === this.userStatuses.active || this.user.status === this.userStatuses.passwordReset) &&
      !this.user.samlProvider;
  }

  get canResendInvitation() {
    return this.canManage &&
      this.user.status === this.userStatuses.invitationSent && !this.user.samlProvider;
  }

  get canEdit() {
    return this.canManage;
  }

  get canViewChangeLog() {
    return true;
  }
}
